/**
 * V2-702.5: Analytics page (V1-parity port).
 *
 * Layout (mirrors V1 screenshots):
 *   1. KPI strip                                      (Won / Lost / No fill / …)
 *   2. Filter bar                                     (Date | Mode | Status chips | Show hidden | Reset)
 *   3. Two-pane: virtualized window list │ detail panel
 *
 * All data loads through the new /v2/analytics/* endpoints; the detail
 * pane wires the Re-fetch / Edit / Hide modals to their backend writers
 * (see AnalyticsActionModals.tsx).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { IconAnchor, IconRefresh, IconRestore } from "@tabler/icons-react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";

import { api } from "../api/client";
import type { AnalyticsKpis, AnalyticsWindowSummary, BetStatus } from "../api/schemas";
import { EmptyState, ErrorState, LoadingState } from "../components/ui";
import { useMarket } from "../hooks/useMarket";
import { monoStyle } from "../theme";
import { AnalyticsDetailPane } from "./AnalyticsDetail";

const ROW_HEIGHT = 64;
const ANALYTICS_LIST_POLL_MS = 10_000;
const RESULTS_PAGE_SIZE = 50;

type ModeFilter = "all" | "live" | "paper";

const MODE_OPTIONS: Array<{ value: ModeFilter; label: string }> = [
  { value: "all",   label: "All" },
  { value: "live",  label: "Live" },
  { value: "paper", label: "Paper" },
];

const STATUS_CHIPS: Array<{ value: BetStatus; label: string; color: string }> = [
  { value: "won",        label: "Won",        color: "green" },
  { value: "lost",       label: "Lost",       color: "red" },
  { value: "no_fill",    label: "No fill",    color: "gray" },
  { value: "skipped",    label: "Skipped",    color: "yellow" },
  { value: "no_bet",     label: "No bet",     color: "dark" },
  { value: "pending",    label: "Pending",    color: "blue" },
];

interface Filters {
  from: string;
  to: string;
  mode: ModeFilter;
  manual_buy_active: boolean;
  active_statuses: BetStatus[];
  stop_loss_active: boolean;
  take_profit_active: boolean;
  hidden_active: boolean;
  // Counterpart (BTC<->ETH) filter chips. Two sub-groups: "traded / not
  // traded" and "same / opposite close direction" relative to this window.
  // OR within each sub-group, AND across sub-groups; all-on = no filtering.
  cp_traded_active: boolean;
  cp_not_traded_active: boolean;
  cp_same_dir_active: boolean;
  cp_opposite_dir_active: boolean;
}

const DEFAULT_FILTERS: Filters = {
  from: "",
  to: "",
  mode: "all",
  manual_buy_active: true,
  active_statuses: STATUS_CHIPS.map((s) => s.value),
  stop_loss_active: true,
  take_profit_active: true,
  hidden_active: true,
  cp_traded_active: true,
  cp_not_traded_active: true,
  cp_same_dir_active: true,
  cp_opposite_dir_active: true,
};

/** True when the counterpart window had an actual order (any fill/attempt). */
function counterpartTraded(row: AnalyticsWindowSummary): boolean {
  const cs = row.counterpart_bet_status;
  return cs === "won" || cs === "lost" || cs === "sold_early" || cs === "no_fill";
}

/**
 * Counterpart filter predicate. Sub-group 1 gates on whether the counterpart
 * was traded; sub-group 2 gates on whether it closed the same/opposite
 * direction as this window. When the close comparison is unknown (either side
 * missing a winning_direction) the row only survives if BOTH direction chips
 * are enabled (i.e. the operator hasn't narrowed to same-only / opposite-only).
 */
function counterpartMatch(row: AnalyticsWindowSummary, f: Filters): boolean {
  const traded = counterpartTraded(row);
  const g1 = (f.cp_traded_active && traded) || (f.cp_not_traded_active && !traded);
  if (!g1) return false;

  const myDir = row.winning_direction;
  const cpDir = row.counterpart_winning_direction;
  const known = myDir != null && cpDir != null;
  const same = known && myDir === cpDir;
  const opposite = known && myDir !== cpDir;
  return (
    (f.cp_same_dir_active && same) ||
    (f.cp_opposite_dir_active && opposite) ||
    (!known && f.cp_same_dir_active && f.cp_opposite_dir_active)
  );
}

function toIsoOrUndef(v: string): string | undefined {
  if (!v) return undefined;
  const d = new Date(v);
  return Number.isFinite(d.getTime()) ? d.toISOString() : undefined;
}

function todayStartLocalInput(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T00:00`;
}

function windowSelectionKey(row: AnalyticsWindowSummary): string {
  // Stable across list re-orders (e.g. a new window prepended on rollover)
  // so the currently-selected row keeps focus.
  return [
    row.slug,
    row.window_start,
    row.window_end,
    row.order_id ?? "no-order",
    row.config_version_id,
  ].join("|");
}

function parseSlugFromUrl(): string | null {
  if (typeof window === "undefined") return null;
  const tryFrom = (qs: string): string | null => {
    if (!qs) return null;
    const sp = new URLSearchParams(qs.startsWith("?") ? qs.slice(1) : qs);
    const s = sp.get("slug");
    return s && s.trim() ? s : null;
  };
  const fromSearch = tryFrom(window.location.search);
  if (fromSearch) return fromSearch;
  const hash = window.location.hash || "";
  const qIdx = hash.indexOf("?");
  return qIdx >= 0 ? tryFrom(hash.slice(qIdx)) : null;
}

function windowDedupeKey(row: AnalyticsWindowSummary): string {
  return row.slug;
}

export function AnalyticsPage() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [visibleResultPages, setVisibleResultPages] = useState(1);
  const qc = useQueryClient();
  const fromIso = toIsoOrUndef(filters.from);
  const toIso = toIsoOrUndef(filters.to);
  const { marketId, market } = useMarket();
  // V3 multi-market: the page title follows the global MarketSwitcher
  // selection so the operator always knows which market they're looking
  // at. The data query above is already keyed on `marketId`.
  const titleMarket = market?.display_name ?? market?.short_name ?? marketId;

  const q = useInfiniteQuery({
    queryKey: ["analytics", "windows", marketId, filters],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.analytics.list({
      cursor: pageParam,
      limit: 50,
      ...(fromIso ? { from: fromIso } : {}),
      ...(toIso ? { to: toIso } : {}),
      mode: filters.mode,
      bet_status: "all",
      show_hidden: true,
      market_id: marketId,
    }),
    getNextPageParam: (last) => (last.page.has_more ? last.page.next_cursor ?? undefined : undefined),
    staleTime: 30_000,
    refetchInterval: ANALYTICS_LIST_POLL_MS,
    refetchIntervalInBackground: true,
  });

  const rawItems = q.data?.pages.flatMap((p) => p.items) ?? [];
  const dedupedRawItems = useMemo(() => {
    const seen = new Set<string>();
    return rawItems.filter((row) => {
      const key = windowDedupeKey(row);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rawItems]);
  const universeItems = dedupedRawItems;
  const kpis: AnalyticsKpis | undefined = q.data?.pages[0]?.kpis;
  const manualBuyCount = kpis?.manual_buy ?? 0;
  const activeStatusSet = new Set(filters.active_statuses);
  const anyFilterActive = activeStatusSet.size > 0
    || filters.stop_loss_active
    || filters.take_profit_active
    || filters.hidden_active
    || filters.manual_buy_active;
  const items = anyFilterActive
    ? universeItems.filter((r) => {
      // Primary (status/exit/hidden/manual) group: OR semantics.
      const statusMatch =
        activeStatusSet.has(r.bet_status)
        || (filters.stop_loss_active && r.exit_reason === "stop_loss")
        || (filters.take_profit_active && r.exit_reason === "take_profit")
        || (filters.hidden_active && r.hidden)
        || (filters.manual_buy_active && r.is_manual_buy);
      if (!statusMatch) return false;
      // Counterpart group is ANDed on top of the primary match.
      return counterpartMatch(r, filters);
    })
    : [];
  // Counterpart chip counts, computed over the loaded universe for this market.
  const cpCounts = useMemo(() => {
    let traded = 0, notTraded = 0, same = 0, opposite = 0;
    for (const r of universeItems) {
      if (counterpartTraded(r)) traded++; else notTraded++;
      const known = r.winning_direction != null && r.counterpart_winning_direction != null;
      if (known && r.winning_direction === r.counterpart_winning_direction) same++;
      else if (known) opposite++;
    }
    return { traded, notTraded, same, opposite };
  }, [universeItems]);
  const keyedItems = useMemo(
    () => items.map((row) => ({ row, rowKey: windowSelectionKey(row) })),
    [items],
  );
  const visibleItemTarget = visibleResultPages * RESULTS_PAGE_SIZE;
  const visibleKeyedItems = useMemo(
    () => keyedItems.slice(0, visibleItemTarget),
    [keyedItems, visibleItemTarget],
  );
  const resultsTotalCount = useMemo(() => {
    if (!kpis) return keyedItems.length;
    const buckets: number[] = [];
    if (filters.active_statuses.length > 0) {
      buckets.push(filters.active_statuses.reduce((acc, s) => acc + kpis[s], 0));
    }
    if (filters.stop_loss_active) buckets.push(kpis.stop_loss);
    if (filters.take_profit_active) buckets.push(kpis.take_profit);
    if (filters.hidden_active) buckets.push(kpis.hidden);
    if (filters.manual_buy_active) buckets.push(kpis.manual_buy);
    if (buckets.length === 0) return 0;
    // Different buckets overlap (e.g. hidden + lost). We can't compute exact
    // union from aggregate KPIs alone, so use the largest active bucket as a
    // conservative count and never show less than currently matched rows.
    return Math.max(Math.max(...buckets), keyedItems.length);
  }, [kpis, filters.active_statuses, filters.stop_loss_active, filters.take_profit_active, filters.hidden_active, filters.manual_buy_active, keyedItems.length]);
  const hasMoreForList = visibleKeyedItems.length < resultsTotalCount || Boolean(q.hasNextPage);

  const urlSlug = useMemo(() => parseSlugFromUrl(), []);
  const [urlConsumed, setUrlConsumed] = useState(false);

  useEffect(() => {
    setVisibleResultPages(1);
  }, [
    fromIso,
    toIso,
    filters.mode,
    filters.stop_loss_active,
    filters.take_profit_active,
    filters.hidden_active,
    filters.manual_buy_active,
    filters.active_statuses,
    filters.cp_traded_active,
    filters.cp_not_traded_active,
    filters.cp_same_dir_active,
    filters.cp_opposite_dir_active,
  ]);

  useEffect(() => {
    if (q.isLoading || q.isFetchingNextPage) return;
    if (!q.hasNextPage) return;
    if (keyedItems.length >= Math.min(visibleItemTarget, resultsTotalCount)) return;
    void q.fetchNextPage();
  }, [q.isLoading, q.isFetchingNextPage, q.hasNextPage, q.fetchNextPage, keyedItems.length, visibleItemTarget, resultsTotalCount]);

  useEffect(() => {
    if (!urlSlug || urlConsumed) return;
    const match = keyedItems.find((r) => r.row.slug === urlSlug);
    if (match) {
      setSelectedKey(match.rowKey);
      setSelectedSlug(urlSlug);
      setUrlConsumed(true);
      return;
    }
    if (q.isFetchingNextPage || q.isLoading) return;
    if (q.hasNextPage) {
      void q.fetchNextPage();
      return;
    }
    // Exhausted: still load detail by slug so the right pane is correct.
    setSelectedSlug(urlSlug);
    setUrlConsumed(true);
  }, [urlSlug, urlConsumed, keyedItems, q.hasNextPage, q.isFetchingNextPage, q.isLoading, q.fetchNextPage]);
  // Auto-select first row when the visible list changes (but never override a
  // user's choice on window rollover — `windowSelectionKey` is now stable).
  const awaitingUrlSelection = Boolean(urlSlug) && !urlConsumed;
  if (!awaitingUrlSelection) {
    if (keyedItems.length > 0 && (!selectedKey || !keyedItems.find((r) => r.rowKey === selectedKey))) {
      setSelectedKey(keyedItems[0]!.rowKey);
      setSelectedSlug(keyedItems[0]!.row.slug);
    }
    if (keyedItems.length === 0 && (selectedKey || selectedSlug)) {
      setSelectedKey(null);
      setSelectedSlug(null);
    }
  }

  const reset = () => setFilters(DEFAULT_FILTERS);

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={2}>
          <Title order={1}>{titleMarket} — Market Data</Title>
          <Text c="dimmed" size="lg">
            Explore window outcomes, filter execution states, and inspect per-window execution detail.
          </Text>
        </Stack>
        <Button
          size="md"
          variant="light"
          leftSection={<IconRefresh size={18} />}
          onClick={() => qc.invalidateQueries({ queryKey: ["analytics"] })}
        >Refresh Data</Button>
      </Group>

      <FilterBar
        kpis={kpis}
        manualBuyCount={manualBuyCount}
        cpCounts={cpCounts}
        filters={filters}
        toggleStatus={(s) => setFilters((f) => {
          const has = f.active_statuses.includes(s);
          return {
            ...f,
            active_statuses: has
              ? f.active_statuses.filter((x) => x !== s)
              : [...f.active_statuses, s],
          };
        })}
        toggleStopLoss={() => setFilters((f) => ({ ...f, stop_loss_active: !f.stop_loss_active }))}
        toggleTakeProfit={() => setFilters((f) => ({ ...f, take_profit_active: !f.take_profit_active }))}
        toggleHidden={() => setFilters((f) => ({ ...f, hidden_active: !f.hidden_active }))}
        toggleManualBuy={() => setFilters((f) => ({ ...f, manual_buy_active: !f.manual_buy_active }))}
        toggleCpTraded={() => setFilters((f) => ({ ...f, cp_traded_active: !f.cp_traded_active }))}
        toggleCpNotTraded={() => setFilters((f) => ({ ...f, cp_not_traded_active: !f.cp_not_traded_active }))}
        toggleCpSameDir={() => setFilters((f) => ({ ...f, cp_same_dir_active: !f.cp_same_dir_active }))}
        toggleCpOppositeDir={() => setFilters((f) => ({ ...f, cp_opposite_dir_active: !f.cp_opposite_dir_active }))}
        setFilters={setFilters}
        onReset={reset}
      />

      <Grid>
        <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
          <Card
            padding={0}
            style={{
              position: "sticky",
              top: 84,
              height: "calc(100vh - 120px)",
            }}
          >
            <WindowList
              items={visibleKeyedItems}
              totalCount={resultsTotalCount}
              isLoading={q.isLoading}
              isError={q.isError}
              error={q.error as Error | undefined}
              selectedKey={selectedKey}
              hasMore={hasMoreForList}
              isFetchingNextPage={q.isFetchingNextPage}
              onLoadMore={() => {
                setVisibleResultPages((n) => n + 1);
                if (q.hasNextPage && !q.isFetchingNextPage) {
                  void q.fetchNextPage();
                }
              }}
              onSelect={(rowKey, slug) => {
                const scrollY = typeof window !== "undefined" ? window.scrollY : 0;
                setSelectedKey(rowKey);
                setSelectedSlug(slug);
                if (typeof window !== "undefined") {
                  requestAnimationFrame(() => {
                    window.scrollTo({ top: scrollY, behavior: "auto" });
                  });
                }
              }}
              onRetry={() => q.refetch()}
            />
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
          {selectedSlug ? (
            <AnalyticsDetailPane slug={selectedSlug} />
          ) : (
            <Card withBorder padding="xl">
              <EmptyState label="No window selected — adjust filters or pick a row from the list." />
            </Card>
          )}
        </Grid.Col>
      </Grid>
    </Stack>
  );
}

// ----------------------------------------------------------------- Filter Bar

function FilterBar({ kpis, manualBuyCount, cpCounts, filters, toggleStatus, toggleStopLoss, toggleTakeProfit, toggleHidden, toggleManualBuy, toggleCpTraded, toggleCpNotTraded, toggleCpSameDir, toggleCpOppositeDir, setFilters, onReset }:
  {
    kpis: AnalyticsKpis | undefined;
    manualBuyCount: number;
    cpCounts: { traded: number; notTraded: number; same: number; opposite: number };
    filters: Filters;
    toggleStatus: (s: BetStatus) => void;
    toggleStopLoss: () => void;
    toggleTakeProfit: () => void;
    toggleHidden: () => void;
    toggleManualBuy: () => void;
    toggleCpTraded: () => void;
    toggleCpNotTraded: () => void;
    toggleCpSameDir: () => void;
    toggleCpOppositeDir: () => void;
    setFilters: React.Dispatch<React.SetStateAction<Filters>>;
    onReset: () => void;
  }) {
  const chipLabelStyles = {
    label: {
      fontWeight: 700,
    },
    iconWrapper: {
      display: "none",
    },
    checkIcon: {
      display: "none",
    },
  };

  const offStyles = {
    label: {
      borderColor: "transparent",
    },
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Group gap="md" wrap="wrap" align="flex-end">
          <Stack gap={4}>
            <Text size="sm" c="dimmed" fw={700} tt="uppercase">Start</Text>
            <TextInput
              size="md"
              type="datetime-local"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.currentTarget.value }))}
              w={230}
            />
          </Stack>

          <Button
            size="md"
            variant="light"
            onClick={() => setFilters((f) => ({ ...f, from: todayStartLocalInput() }))}
          >Today</Button>

          <Stack gap={4}>
            <Text size="sm" c="dimmed" fw={700} tt="uppercase">End</Text>
            <TextInput
              size="md"
              type="datetime-local"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.currentTarget.value }))}
              w={230}
            />
          </Stack>

          <Stack gap={4}>
            <Text size="sm" c="dimmed" fw={700} tt="uppercase">Mode</Text>
            <SegmentedControl
              size="md"
              value={filters.mode}
              onChange={(v) => setFilters((f) => ({ ...f, mode: (v as ModeFilter) ?? "all" }))}
              data={MODE_OPTIONS}
            />
          </Stack>

          <Button
            size="md"
            variant="default"
            leftSection={<IconRestore size={18} />}
            onClick={onReset}
          >Reset filters</Button>
        </Group>

        <Group gap="xs" wrap="wrap">
          {STATUS_CHIPS.map((c) => {
            const active = filters.active_statuses.includes(c.value);
            const count = kpis ? kpis[c.value] : 0;
            return (
              <Chip
                key={c.value}
                checked={active}
                onChange={() => toggleStatus(c.value)}
                size="sm"
                radius="xl"
                icon={null}
                variant={active ? "filled" : "light"}
                color={active ? c.color : "gray"}
                styles={active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
              >
                {`${c.label} - ${count}`}
              </Chip>
            );
          })}

          <Chip
            checked={filters.stop_loss_active}
            onChange={toggleStopLoss}
            size="sm"
            radius="xl"
            icon={null}
            variant={filters.stop_loss_active ? "filled" : "light"}
            color={filters.stop_loss_active ? "orange" : "gray"}
            styles={filters.stop_loss_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Stop-loss - ${kpis?.stop_loss ?? 0}`}
          </Chip>

          <Chip
            checked={filters.take_profit_active}
            onChange={toggleTakeProfit}
            size="sm"
            radius="xl"
            icon={null}
            variant={filters.take_profit_active ? "filled" : "light"}
            color={filters.take_profit_active ? "teal" : "gray"}
            styles={filters.take_profit_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Take-profit - ${kpis?.take_profit ?? 0}`}
          </Chip>

          <Chip
            checked={filters.hidden_active}
            onChange={toggleHidden}
            size="sm"
            radius="xl"
            icon={null}
            variant={filters.hidden_active ? "filled" : "light"}
            color={filters.hidden_active ? "dark" : "gray"}
            styles={filters.hidden_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Hidden - ${kpis?.hidden ?? 0}`}
          </Chip>

          <Chip
            checked={filters.manual_buy_active}
            onChange={toggleManualBuy}
            size="sm"
            radius="xl"
            icon={null}
            variant={filters.manual_buy_active ? "filled" : "light"}
            color={filters.manual_buy_active ? "orange" : "gray"}
            styles={filters.manual_buy_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Manual Buy - ${manualBuyCount}`}
          </Chip>
        </Group>

        {/* Counterpart (BTC<->ETH) filter chips. */}
        <Group gap="xs" wrap="wrap" align="center">
          <Text size="sm" c="dimmed" fw={700} tt="uppercase">Counterpart</Text>
          <Chip
            checked={filters.cp_traded_active}
            onChange={toggleCpTraded}
            size="sm" radius="xl" icon={null}
            variant={filters.cp_traded_active ? "filled" : "light"}
            color={filters.cp_traded_active ? "blue" : "gray"}
            styles={filters.cp_traded_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Traded - ${cpCounts.traded}`}
          </Chip>
          <Chip
            checked={filters.cp_not_traded_active}
            onChange={toggleCpNotTraded}
            size="sm" radius="xl" icon={null}
            variant={filters.cp_not_traded_active ? "filled" : "light"}
            color={filters.cp_not_traded_active ? "grape" : "gray"}
            styles={filters.cp_not_traded_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Not traded - ${cpCounts.notTraded}`}
          </Chip>
          <Chip
            checked={filters.cp_same_dir_active}
            onChange={toggleCpSameDir}
            size="sm" radius="xl" icon={null}
            variant={filters.cp_same_dir_active ? "filled" : "light"}
            color={filters.cp_same_dir_active ? "teal" : "gray"}
            styles={filters.cp_same_dir_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Same dir - ${cpCounts.same}`}
          </Chip>
          <Chip
            checked={filters.cp_opposite_dir_active}
            onChange={toggleCpOppositeDir}
            size="sm" radius="xl" icon={null}
            variant={filters.cp_opposite_dir_active ? "filled" : "light"}
            color={filters.cp_opposite_dir_active ? "red" : "gray"}
            styles={filters.cp_opposite_dir_active ? chipLabelStyles : { ...chipLabelStyles, ...offStyles }}
          >
            {`Opposite dir - ${cpCounts.opposite}`}
          </Chip>
        </Group>
      </Stack>
    </Card>
  );
}

// ------------------------------------------------------------- Virtualized list

interface ListProps {
  items: Array<{ row: AnalyticsWindowSummary; rowKey: string }>;
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
  error?: Error;
  selectedKey: string | null;
  hasMore: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
  onSelect: (rowKey: string, slug: string) => void;
  onRetry: () => void;
}

function WindowList(p: ListProps) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const virtualizer = useVirtualizer({
    count: p.items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
  });

  return (
    <Stack h="100%" gap={0}>
      <Group justify="space-between" px="lg" py="md" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
        <Text size="lg" fw={700}>Results</Text>
        <Text size="lg" c="dimmed" style={monoStyle}>{p.totalCount}</Text>
      </Group>

      {p.isLoading ? <LoadingState /> :
       p.isError ? <ErrorState error={p.error ?? null} onRetry={p.onRetry} /> :
       p.items.length === 0 ? (
        <Stack gap="sm" p="md" align="center">
          <EmptyState label="No windows match your filters." />
          {p.hasMore ? (
            <Button
              size="sm"
              variant="light"
              onClick={p.onLoadMore}
              loading={p.isFetchingNextPage}
            >
              Load more
            </Button>
          ) : null}
        </Stack>
      ) : (
        <>
          <ScrollArea viewportRef={parentRef} flex={1} type="hover">
            <Box style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((vrow) => {
                const entry = p.items[vrow.index];
                if (!entry) return null;
                const active = entry.rowKey === p.selectedKey;
                return (
                  <ListRow
                    key={entry.rowKey}
                    row={entry.row}
                    active={active}
                    onClick={() => p.onSelect(entry.rowKey, entry.row.slug)}
                    style={{
                      position: "absolute",
                      top: 0, left: 0, width: "100%",
                      height: ROW_HEIGHT,
                      transform: `translateY(${vrow.start}px)`,
                    }}
                  />
                );
              })}
            </Box>
          </ScrollArea>
          <Group justify="center" p="sm" style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
            {p.hasMore ? (
              <Button
                size="sm"
                variant="light"
                onClick={p.onLoadMore}
                loading={p.isFetchingNextPage}
              >
                Load more
              </Button>
            ) : null}
          </Group>
        </>
      )}
    </Stack>
  );
}

function ListRow({ row, active, onClick, style }: {
  row: AnalyticsWindowSummary; active: boolean; onClick: () => void; style: React.CSSProperties;
}) {
  const start = new Date(row.window_start);
  const end = new Date(row.window_end);
  const fmt = (d: Date) => `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const delta = row.btc_delta ? Number(row.btc_delta) : null;
  const positive = delta != null && delta >= 0;
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        ...style,
        display: "flex", alignItems: "center", padding: "12px 14px", gap: 12,
        cursor: "pointer", userSelect: "none",
        background: active ? "var(--mantine-color-blue-light)" : "transparent",
        borderBottom: "1px solid var(--mantine-color-default-border)",
        overflow: "hidden",
      }}
    >
      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
        <Group justify="space-between" wrap="nowrap" gap={6} style={{ minWidth: 0 }}>
          <Text size="sm" fw={700} style={{ ...monoStyle, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {fmt(start)}–{fmt(end).slice(-5)}
          </Text>
          <Group gap={4} wrap="nowrap">
            {row.counterpart_override_active ? (
              <Tooltip label="Counterpart Override or Follow mode active at this order's entry" withArrow>
                <IconRefresh
                  size={14}
                  color="var(--mantine-color-orange-5)"
                  aria-label="Counterpart Market Override"
                />
              </Tooltip>
            ) : null}
            {row.favorite_hold_tier ? (
              <Tooltip label={`Entered via Favorite Hold tier ${row.favorite_hold_tier.replace("tier", "")}`} withArrow>
                <IconAnchor
                  size={14}
                  color="var(--mantine-color-teal-5)"
                  aria-label="Favorite Hold"
                />
              </Tooltip>
            ) : null}
            {row.is_manual_buy ? <Badge size="xs" color="orange">manual</Badge> : null}
            {row.hidden ? <Badge size="xs" color="gray">hidden</Badge> : null}
          </Group>
        </Group>
        <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
          {delta != null ? (
            <Text size="sm" fw={700} style={{ ...monoStyle, whiteSpace: "nowrap", color: positive ? "var(--mantine-color-profit-5)" : "var(--mantine-color-loss-5)" }}>
              {positive ? "↑" : "↓"}${Math.abs(delta).toFixed(2)}
            </Text>
          ) : <Text size="sm" c="dimmed">—</Text>}
          <Text size="sm" c="dimmed">•</Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {prettyBetStatus(row.bet_status)}
          </Text>
          <CounterpartTag row={row} />
        </Group>
      </Stack>
    </UnstyledButton>
  );
}

/**
 * Compact counterpart (BTC<->ETH) indicator shown on windows that had an
 * order: the opposite-asset market's result (won/lost/no trade) plus the
 * direction it closed. Hidden for windows with no order of their own.
 */
function CounterpartTag({ row }: { row: AnalyticsWindowSummary }) {
  if (row.order_id == null) return null;
  const asset = row.counterpart_market_id
    ? row.counterpart_market_id.split("-")[0]!.toUpperCase()
    : "CTPT";
  const cs = row.counterpart_bet_status;
  const dir = row.counterpart_winning_direction;
  // No counterpart window tracked for this timestamp.
  if (!row.counterpart_slug || (cs == null && dir == null)) {
    return <Badge size="xs" variant="light" color="gray" title={`No ${asset} counterpart window`}>{asset} n/a</Badge>;
  }
  const traded = cs === "won" || cs === "lost" || cs === "sold_early" || cs === "no_fill";
  const resultLabel =
    cs === "won" ? "Won" :
    cs === "lost" ? "Lost" :
    cs === "sold_early" ? "Sold" :
    cs === "no_fill" ? "No fill" :
    "No trade";
  const color = cs === "won" ? "green" : cs === "lost" ? "red" : "gray";
  const arrow = dir === "UP" ? "↑" : dir === "DOWN" ? "↓" : "";
  const same = row.winning_direction != null && dir != null
    ? row.winning_direction === dir
    : null;
  const dirNote = dir ? `, closed ${dir}` : "";
  const sameNote = same == null ? "" : same ? " (same direction)" : " (opposite direction)";
  return (
    <Badge
      size="xs"
      variant={traded ? "filled" : "light"}
      color={color}
      style={{ whiteSpace: "nowrap" }}
      title={`Counterpart ${asset}: ${resultLabel}${dirNote}${sameNote}`}
    >
      {`${asset} ${arrow} ${resultLabel}`.replace(/\s+/g, " ").trim()}
    </Badge>
  );
}

function prettyBetStatus(s: BetStatus): string {
  if (s === "no_fill")   return "No fill";
  if (s === "no_bet")    return "No bet";
  if (s === "sold_early") return "Sold early";
  return s[0]!.toUpperCase() + s.slice(1);
}

// expose useMemo to satisfy linter (used elsewhere in file)
void useMemo;
